import React, { useState, useEffect, useRef } from "react";
import { Spinner, Button, Alert, Form } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import OutwardLetter from "./OutwardLetter";
import { generatePDF } from "./pdfGenerator";
import instance from "../../services/baseService";
import toast from "react-hot-toast";
import useCheckAuthorization from "../../hooks/checkAuthorization";
import { CRUD_PERMISSIONS } from "../../constants/user";
import Select from "react-select";
import { IoMdRefresh } from "react-icons/io";

const OutwardTab = ({ clientId, reportId, type, port_code_id }) => {
    const [loading, setLoading] = useState(true);
    const [letterData, setLetterData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const letterRef = useRef();
    const approvePermission = useCheckAuthorization(
        CRUD_PERMISSIONS.reconciliation.approve
    );

    const [clientOptions, setClientOptions] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Reset all states to initial values
    const resetComponent = () => {
        setLetterData(null);
        setSelectedClient(null);
        setClientOptions([]);
        setError(null);
        setSearchQuery("");
    };

    // Fetch client details - latestData always 0, backend handles the logic
    const getClientDetails = async (id) => {
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/get-data/${id}/${reportId}`,
                {
                    port_code_id: port_code_id,
                    type: "outward",
                    latestData: 0,
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching client details:", error);
            throw error;
        }
    };

    // Fetch clients list
    const fetchClients = async (search = "") => {
        setLoadingClients(true);
        try {
            const searchParam = search || "-";
            const response = await instance.get(
                `/api/v1/reconciliation/clientList/transfer-letter/1/10/${encodeURIComponent(
                    searchParam
                )}?period_id=${reportId}&port_code_id=${port_code_id}&type=outward`
            );
            const options = response.data?.data?.lists.map((client) => ({
                value: client.id,
                label: client.client_name,
                data: client,
            }));
            setClientOptions(options);
            return options;
        } catch (err) {
            console.error("Error fetching clients:", err);
            toast.error("Failed to load clients");
            return [];
        } finally {
            setLoadingClients(false);
        }
    };

    // Auto-select first client and fetch its data
    useEffect(() => {
        if (clientOptions.length > 0 && !selectedClient) {
            const firstClient = clientOptions[0];
            setSelectedClient(firstClient);

            if (firstClient?.value) {
                setLoading(true);
                getClientDetails(firstClient.value)
                    .then((response) => {
                        const clientDetails = response.data;
                        setLetterData({
                            client_id: firstClient.value,
                            client_name: clientDetails.client_name,
                            address: clientDetails.address,
                            gstin: clientDetails.gstin,
                            date: clientDetails.date,
                            generated: clientDetails.generated || 0,
                            approved: clientDetails.approved || 0,
                            rejected: clientDetails.rejected || 0,
                            dated: clientDetails.dated,
                            icegate_id: clientDetails.icegate_id,
                            escrip_no: clientDetails.escrip_no,
                            amount: clientDetails.amount,
                            port_code: clientDetails.port_code,
                            iec: clientDetails.iec,
                            file: clientDetails.file || null,
                        });
                    })
                    .catch((err) => {
                        console.error("Error fetching client details:", err);
                        toast.error("Failed to load client details");
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    }, [clientOptions, selectedClient]);

    // Handle client selection from dropdown
    const handleClientSelect = async (selectedOption) => {
        setSelectedClient(selectedOption);

        if (selectedOption?.value) {
            setLoading(true);
            try {
                const response = await getClientDetails(selectedOption.value);
                const clientDetails = response.data;

                setLetterData({
                    client_id: selectedOption.value,
                    client_name: clientDetails.client_name,
                    address: clientDetails.address,
                    gstin: clientDetails.gstin,
                    date: clientDetails.date,
                    generated: clientDetails.generated || 0,
                    approved: clientDetails.approved || 0,
                    rejected: clientDetails.rejected || 0,
                    dated: clientDetails.dated,
                    icegate_id: clientDetails.icegate_id,
                    escrip_no: clientDetails.escrip_no,
                    amount: clientDetails.amount,
                    port_code: clientDetails.port_code,
                    iec: clientDetails.iec,
                    file: clientDetails.file || null,
                });
            } catch (err) {
                console.error("Error fetching client details:", err);
                toast.error("Failed to load client details");
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle search input change
    const handleSearchChange = (inputValue) => {
        setSearchQuery(inputValue);
        if (inputValue.length >= 2) {
            fetchClients(inputValue);
        } else if (inputValue.length === 0) {
            fetchClients();
        }
    };

    const initializeComponent = async () => {
        const options = await fetchClients();
        if (!options || options.length === 0) {
            setLoading(false);
        }
    };

    // Initialize on mount
    useEffect(() => {
        if (reportId && port_code_id) {
            initializeComponent();
        }
    }, [reportId, port_code_id]);

    // Handle Generate PDF
    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const pdfBlob = await generatePDF(letterRef.current, letterData);

            const formData = new FormData();
            formData.append(
                "pdf",
                pdfBlob,
                `Transfer_Letter_${type}_${Date.now()}.pdf`
            );
            formData.append("type", type);
            formData.append("client_id", selectedClient?.value || "");
            formData.append("client_name", letterData.client_name || "");
            formData.append("address", letterData.address || "");
            formData.append("gstin", letterData.gstin || "");
            formData.append("icegate_id", letterData.icegate_id || "");
            formData.append("iec", letterData.iec || "");
            formData.append("date", letterData.date || "");
            formData.append("amount", letterData.amount || "");

            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/generate/${clientId}/${reportId}/${port_code_id}/${letterData?.client_id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.type === "success") {
                setLetterData({
                    ...letterData,
                    generated: 1,
                    file: response.data?.data?.file_url,
                    client_id: selectedClient?.value,
                });
                toast.success(response.data.message);
            } else {
                setError(response.data.message || "Failed to generate PDF");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to generate PDF");
            console.error(err);
        }
        setGenerating(false);
    };

    // Handle Approve
    const handleApprove = async () => {
        setApproving(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/approve-reject/${clientId}/${reportId}/${port_code_id}`,
                {
                    type: type,
                    action: "approve",
                }
            );

            if (response.data.type === "success") {
                setLetterData({
                    ...letterData,
                    approved: 1,
                    rejected: 0,
                });
                toast.success(response.data.message);
            } else {
                setError(response.data.message || "Failed to approve letter");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to approve letter");
            console.error(err);
        }
        setApproving(false);
    };

    // Handle Reject
    const handleReject = async () => {
        if (
            !window.confirm(
                "Are you sure you want to reject this letter?"
            )
        ) {
            return;
        }
        setRejecting(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/approve-reject/${clientId}/${reportId}/${port_code_id}`,
                {
                    type: type,
                    action: "reject",
                }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message);
                // Reset and re-initialize
                resetComponent();
                await initializeComponent();
            } else {
                setError(response.data.message || "Failed to reject letter");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to reject letter");
            console.error(err);
        }
        setRejecting(false);
    };

    // Handle Download
    const handleDownload = () => {
        try {
            if (!letterData?.file) {
                throw new Error("No file URL found");
            }

            const link = document.createElement("a");
            link.href = letterData.file;
            link.download = `${letterData.client_name}_${letterData.type}.pdf`;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error(err);
            setError("Failed to download the file");
        } finally {
            setDownloading(false);
        }
    };

    // Handle Regenerate
    const handleRegenerate = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/refresh/${clientId}/${reportId}/${port_code_id}`,
                { type: "outward" }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message);
                // Reset and re-initialize
                resetComponent();
                await initializeComponent();
            } else {
                setError(response.data.message || "Failed to regenerate letter");
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                err.message ||
                "Failed to regenerate letter"
            );
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };

    if (loading || refreshing) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" />
            </div>
        );
    }

    const hasData = Boolean(letterData && Object.keys(letterData).length > 0);
    const isDataComplete = Boolean(
        letterData &&
        [
            "client_name",
            "address",
            "gstin",
            "icegate_id",
            "iec",
            "date",
            "dated",
            "escrip_no",
            "amount",
            "port_code",
        ].every((key) => letterData[key] !== null && letterData[key] !== "")
    );
    const showGenerateButton = isDataComplete && letterData.generated === 0;
    const showDownloadButton = isDataComplete && letterData.generated === 1;
    const showApproveReject = Boolean(
        hasData &&
        letterData.generated &&
        !letterData.approved &&
        !letterData.rejected
    );
    const showRejectedLabel = Boolean(hasData && letterData.rejected);
    const showApprovedLabel = Boolean(hasData && letterData.approved);

    return (
        <div>
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Client Selection Dropdown */}
            <div className="mb-4">
                <Form.Label className="fw-bold">Select Client</Form.Label>
                <Select
                    options={clientOptions}
                    value={selectedClient}
                    onChange={handleClientSelect}
                    onInputChange={handleSearchChange}
                    isLoading={loadingClients}
                    isSearchable={true}
                    placeholder="Search and select a client..."
                    isDisabled={letterData?.generated === 1}
                    noOptionsMessage={() => "No clients found"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
                {letterData?.generated === 1 && (
                    <small className="text-muted d-block mt-1">
                        Client selection is locked after PDF generation
                    </small>
                )}
            </div>

            {!hasData ? (
                <div className="text-center py-5">
                    <p>No letter data available. Please select a client.</p>
                </div>
            ) : (
                <>
                    {/* Action Buttons - Top */}
                    <div className="d-flex justify-content-between gap-2 mb-3">
                        {showDownloadButton && (
                            <Button
                                variant="link"
                                size="sm"
                                disabled={downloading || generating || refreshing}
                                onClick={handleDownload}
                                className="text-decoration-none d-flex gap-2 align-items-center fw-bold"
                            >
                                <FaDownload /> Download PDF
                            </Button>
                        )}
                        {(showRejectedLabel || showApprovedLabel) && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleRegenerate}
                                disabled={downloading || generating || refreshing}
                                className="text-decoration-none d-flex gap-2 align-items-center fw-bold"
                            >
                                <IoMdRefresh /> Re-generate PDF
                            </Button>
                        )}
                    </div>

                    {!isDataComplete && (
                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <div
                                className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                style={{ backgroundColor: "#fff3cd" }}
                            >
                                Update the required fields to proceed.
                            </div>
                        </div>
                    )}

                    {/* Letter Content */}
                    <div ref={letterRef}>
                        <OutwardLetter data={letterData} />
                    </div>

                    {/* Action Buttons - Bottom */}
                    <div className="d-flex justify-content-center gap-3 mt-4">
                        {showGenerateButton && (
                            <Button
                                variant="primary"
                                className="fw-bold no-hover"
                                onClick={handleGenerate}
                                disabled={generating || refreshing}
                            >
                                {generating ? "Generating..." : "Generate PDF"}
                            </Button>
                        )}

                        {showApproveReject && (
                            <>
                                {approvePermission ? (
                                    <>
                                        <Button
                                            variant="success"
                                            className="fw-bold no-hover"
                                            onClick={handleApprove}
                                            disabled={approving || refreshing}
                                        >
                                            {approving ? "Approving..." : "Approve"}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            className="fw-bold no-hover"
                                            onClick={handleReject}
                                            disabled={rejecting || refreshing}
                                        >
                                            {rejecting ? "Rejecting..." : "Reject"}
                                        </Button>
                                    </>
                                ) : (
                                    <div
                                        className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                        style={{ backgroundColor: "#fff3cd" }}
                                    >
                                        Waiting for Approval
                                    </div>
                                )}
                            </>
                        )}

                        {showRejectedLabel && (
                            <div
                                className="px-3 py-2 rounded-pill fw-bold text-white"
                                style={{ backgroundColor: "#dc3545" }}
                            >
                                Rejected
                            </div>
                        )}

                        {showApprovedLabel && (
                            <div
                                className="px-3 py-2 rounded-pill fw-bold text-white"
                                style={{ backgroundColor: "#28a745" }}
                            >
                                Approved
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default OutwardTab;